/**
 * EmailSearchableTagInput
 * Transforms <input type="text" class="email-multi-select"> into
 * a tag-based email entry field with dropdown suggestions.
 */

const EmailSearchableTagInput = {
  /** Initialize all matching email inputs */
  init() {
    document.querySelectorAll(".email-multi-select").forEach(inputEl => {
      this.initEmailInput(inputEl);
    });
  },

  /** Initialize a single input-based tag email field */
  initEmailInput(inputEl) {
    // Extract initial values as potential suggestions (not selected)
    const allKnownEmails = inputEl.value
      ? inputEl.value.split(",").map(e => e.trim()).filter(Boolean)
      : [];

    let selectedEmails = [];
    inputEl.value = "";

    // === Create wrapper ===
    const wrapper = this.createWrapper(inputEl.id);
    const tagsContainer = wrapper.querySelector(".tags");
    const textInput = wrapper.querySelector(".tag-text-input");
    const dropdown = wrapper.querySelector(".dropdown");

    // Hide original input visually
    inputEl.style.display = "none";
    inputEl.removeAttribute("required");
    inputEl.parentNode.insertBefore(wrapper, inputEl);

    /** ------------------------------
     *         CORE FUNCTIONS
     * ------------------------------ */

    /** Render tags visually */
    function renderTags() {
      tagsContainer.innerHTML = "";
      selectedEmails.forEach(email => {
        const tag = document.createElement("div");
        tag.className = "tag";
        tag.innerHTML = `
          <span>${email}</span>
          <span class="tag-remove-icon">&times;</span>
        `;
        tag.querySelector(".tag-remove-icon").addEventListener("click", () => removeEmail(email));
        tagsContainer.appendChild(tag);
      });
      inputEl.value = selectedEmails.join(", ");
    }

    function processEmails(raw) {
      const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
      const found = raw.match(emailRegex) || [];
      return [...new Set(found.map(e => e.trim()))];
    }

    /** Add a new email tag */
    function addEmail(email) {
      email = email.trim();
      if (!email || selectedEmails.includes(email)) return;
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        alert("Please enter a valid email address");
        return;
      }
      
      selectedEmails.push(email);

      clearErrorState();
      renderTags();
      hideDropdown();
    }

    /** Remove an email tag */
    function removeEmail(email) {
      selectedEmails = selectedEmails.filter(e => e !== email);
      renderTags();
    }

    /** Render dropdown based on filter */
    function renderDropdown(filteredEmails, inputValue) {
      dropdown.innerHTML = "";

      filteredEmails.forEach(email => {
        const li = document.createElement("li");
        li.textContent = email;
        dropdown.appendChild(li);
      });

      dropdown.style.display = "block";
    }

    function hideDropdown() {
      dropdown.style.display = "none";
    }

    function clearErrorState() {
      const parentLi = wrapper.closest("li");

      if (parentLi && parentLi.classList.contains("error")) {
        parentLi.classList.remove("error");
        const oldError = parentLi.querySelector(".inline-errors");
        if (oldError) oldError.remove();
        wrapper.style.borderColor = "";
      }
    }

    function createErrorMessage() {
      const errorMessage = document.querySelector('meta[name="localized-error-blank"]')?.getAttribute("content") || "can't be blank";

      // Remove existing error
      const parentLi = wrapper.closest("li");
    
      // Add the error class to the <li>
      parentLi.classList.add("error");
    
      // Add Rails-style error paragraph
      const errorEl = document.createElement("p");
      errorEl.className = "inline-errors";
      errorEl.textContent = errorMessage;
      parentLi.insertBefore(errorEl, parentLi.querySelector(".inline-hints"));

      wrapper.style.borderColor = "rgb(239 68 68/var(--tw-border-opacity,1))";

      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    /** ------------------------------
     *         EVENT HANDLERS
     * ------------------------------ */

    // Focus anywhere inside container focuses input
    wrapper.addEventListener("click", e => {
      if (e.target === wrapper || e.target.classList.contains("tags")) {
        textInput.focus();
      }
    });

    // Show dropdown on focus
    textInput.addEventListener("focus", () => {
      const available = allKnownEmails.filter(email => !selectedEmails.includes(email));

      if (available.length > 0) {
        renderDropdown(available, "");
      } else {
        hideDropdown();
      }
    });

    textInput.addEventListener("paste", e => {
      setTimeout(() => {
        processEmails(textInput.value).forEach(addEmail);
        textInput.value = "";
      }, 0);
    });

    // Filter dropdown as user types
    textInput.addEventListener("input", e => {
      const value = e.target.value.toLowerCase();
      const filtered = allKnownEmails.filter(
        email => email.toLowerCase().includes(value) && !selectedEmails.includes(email)
      );

      if (filtered.length > 0) {
        renderDropdown(filtered, e.target.value.trim());
      }
      else {
        hideDropdown();
      }
    });

    // Add email on Enter or comma
    textInput.addEventListener("keydown", e => {
      if (["Enter", ",", ";"].includes(e.key)) {
        e.preventDefault();
        processEmails(textInput.value).forEach(addEmail);
        textInput.value = "";
      } else if (e.key === "Backspace" && !textInput.value && selectedEmails.length) {
        removeEmail(selectedEmails[selectedEmails.length - 1]);
      }
    });

    // Handle dropdown click
    dropdown.addEventListener("click", e => {
      if (e.target.tagName === "LI") {
        const text = e.target.textContent.replace(/^Add\s+"|"$/g, "").trim();
        addEmail(text);
        textInput.value = "";
      }
    });

    // Hide dropdown on outside click
    document.addEventListener("click", e => {
      if (!wrapper.contains(e.target)) hideDropdown();
    });

    // Add pending email on blur
    textInput.addEventListener("blur", () => {
      const emails = processEmails(textInput.value);
      emails.forEach(email => addEmail(email));
      textInput.value = "";
    });

    // === Custom validation ===
    const form = inputEl.closest("form");

    if (form) {
      form.addEventListener("submit", e => {
        if (wrapper.classList.contains("input-id-email_message_to_recipients") && selectedEmails.length === 0) {
          e.preventDefault();
          
          clearErrorState();

          createErrorMessage();
          
          setTimeout(() => {
            // Re-enable submit button (Rails disables it automatically)
            const submitBtn = form.querySelector('[data-disable-with]');
            if (submitBtn) submitBtn.disabled = false;
          }, 100);
        }
      });
    }

    // Initial render (none selected)
    renderTags();
  },

  /** ------------------------------
   *         HELPER FUNCTIONS
   * ------------------------------ */

  /** Creates wrapper container */
  createWrapper(inputId) {
    const wrapper = document.createElement("div");
    wrapper.className = `tag-input-container input-id-${inputId}`;
    wrapper.innerHTML = `
      <div class="tags flex flex-wrap gap-1"></div>
      <input type="text" class="tag-text-input" autocomplete="off" placeholder="Type or search..." />
      <ul class="dropdown absolute bg-white border border-gray-300 dark:border-gray-700 rounded-md mt-1 max-h-40 overflow-y-auto hidden z-10"></ul>
    `;
    return wrapper;
  }
};

// Auto-initialize on DOM load
document.addEventListener("DOMContentLoaded", () => EmailSearchableTagInput.init());
